import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { io, Socket } from 'socket.io-client';
import axios from 'axios';
import { useScraperContext } from '../../contexts/ScraperContext';
import { useAuth } from '../../contexts/AuthContext';

interface BulkURLImportProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  onHide?: () => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  onStartImport?: (startFunction: () => void) => void;
  onScraperStatusUpdate?: (status: { isRunning: boolean; processedCount: number; totalCount: number }) => void;
}

interface ContactData {
  first_name: string;
  last_name: string;
  company?: string;
  role?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  sales_navigator_url?: string;
}

interface ScraperStatus {
  isRunning: boolean;
  isConnected: boolean;
  currentPage: {
    url: string;
    html: string;
    contactId?: string;
    timestamp: string;
  } | null;
  currentContactData: ContactData | null;
  processedCount: number;
  totalCount: number;
}

interface SalesRep {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

// Styled Components
const BulkImportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
`;

const BulkImportTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
`;

const BulkImportDescription = styled.p`
  margin: 0 0 1.5rem 0;
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.5;
  text-align: center;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

const UrlCounter = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  text-align: right;
`;

const ProcessingIndicator = styled.div`
  background: #dbeafe;
  border: 1px solid #93c5fd;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
  color: #1d4ed8;
`;

// Scraper Progress Components
const ScraperProgressContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const ScraperProgressTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ScraperProgressDescription = styled.p`
  margin: 0 0 1rem 0;
  color: #64748b;
  font-size: 0.875rem;
`;

const ScraperStatusCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatusLabel = styled.span`
  font-weight: 500;
  color: #475569;
`;

const StatusValue = styled.span<{ $status?: string }>`
  color: ${props => {
    switch (props.$status) {
      case 'connected':
      case 'running':
        return '#059669';
      case 'disconnected':
      case 'stopped':
        return '#dc2626';
      default:
        return '#374151';
    }
  }};
  font-weight: 500;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin: 1rem 0;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 100%;
  background-color: #10b981;
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
`;

const CurrentContactCard = styled.div`
  background: #f0f9ff;
  border: 2px solid #0ea5e9;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

const ContactCardTitle = styled.h4`
  color: #0c4a6e;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ContactDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

const ContactDataItem = styled.div`
  background: white;
  border: 1px solid #e0f2fe;
  border-radius: 8px;
  padding: 12px;
`;

const ContactDataLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

const ContactDataValue = styled.div`
  font-size: 0.875rem;
  color: #1e293b;
  font-weight: 500;
  word-break: break-word;
`;

const LivePreviewContainer = styled.div`
  margin-top: 1rem;
`;

const LivePreviewTitle = styled.h4`
  color: #1e293b;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EmptyStateMessage = styled.div`
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
  padding: 2rem;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
`;

const ControlButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
`;

const ControlButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
          
          &:hover:not(:disabled) {
            background: #2563eb;
            border-color: #2563eb;
          }
          
          &:disabled {
            background: #9ca3af;
            border-color: #9ca3af;
            cursor: not-allowed;
          }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          border-color: #ef4444;
          
          &:hover:not(:disabled) {
            background: #dc2626;
            border-color: #dc2626;
          }
          
          &:disabled {
            background: #9ca3af;
            border-color: #9ca3af;
            cursor: not-allowed;
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border-color: #d1d5db;
          
          &:hover:not(:disabled) {
            background: #f9fafb;
            border-color: #9ca3af;
          }
          
          &:disabled {
            background: #f3f4f6;
            color: #9ca3af;
            cursor: not-allowed;
          }
        `;
    }
  }}
`;

const BulkURLImport: React.FC<BulkURLImportProps> = ({
  onSuccess,
  onCancel,
  onHide,
  isProcessing,
  setIsProcessing,
  onStartImport,
  onScraperStatusUpdate
}) => {
  const { handleScraperStatusUpdate } = useScraperContext();
  const { user } = useAuth();
  const [urlsText, setUrlsText] = useState('');
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [selectedSalesRepId, setSelectedSalesRepId] = useState<string>('current'); // 'current', 'unassigned', or a user ID
  const [scraperStatus, setScraperStatus] = useState<ScraperStatus>({
    isRunning: false,
    isConnected: false,
    currentPage: null,
    currentContactData: null,
    processedCount: 0,
    totalCount: 0
  });
  const [showScraperProgress, setShowScraperProgress] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const currentContactIdRef = useRef<string | null>(null);

  // Check if scraping is completed or stopped
  const isScrapingCompleted = scraperStatus.processedCount >= scraperStatus.totalCount && scraperStatus.totalCount > 0;
  const isScrapingStopped = !scraperStatus.isRunning && scraperStatus.totalCount > 0 && !isScrapingCompleted;

  // Handle stopping the scraper
  const handleStopScraper = async () => {
    try {
      console.log("🛑 Stopping bulk scraper...");
      const response = await axios.post('/api/scraper/stop');
      
      if (response.data.success) {
        console.log("✅ Bulk scraper stopped successfully");
        setScraperStatus(prev => ({ ...prev, isRunning: false }));
      } else {
        console.error("❌ Failed to stop bulk scraper:", response.data.message);
        alert('Impossibile fermare lo scraper: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('❌ Error stopping bulk scraper:', error);
      alert('Errore durante l\'arresto dello scraper: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle closing the modal (for cancel)
  const handleCloseModal = () => {
    console.log("🚪 Closing bulk scraper modal");
    setIsProcessing(false);
    setShowScraperProgress(false);
    if (onCancel) {
      onCancel();
    }
  };

  // Handle hiding the modal (close but keep scraper running)
  const handleHideModal = () => {
    console.log("👁️ Hiding bulk scraper modal (scraper continues running)");
    if (onHide) {
      onHide();
    }
  };

  // Handle done button (close modal and call success callback)
  const handleDone = () => {
    console.log("✅ Bulk import completed - closing modal");
    setIsProcessing(false);
    setShowScraperProgress(false);
    if (onSuccess) {
      onSuccess();
    }
  };

  // Parse URLs from text input
  const getValidUrls = () => {
    return urlsText
      .split('\n')
      .map(url => url.trim())
      .filter(url => url && url.includes('linkedin.com/in/'));
  };

  const validUrls = getValidUrls();
  const isAdmin = user?.role === 'admin';

  // Fetch active users for sales rep dropdown (admin only)
  useEffect(() => {
    if (!isAdmin) return;

    const fetchSalesReps = async () => {
      try {
        const response = await axios.get('/api/users', {
          params: { isActive: true, limit: 100 }
        });
        if (response.data.success) {
          setSalesReps(response.data.data.users || []);
        }
      } catch (error) {
        console.error('Failed to fetch sales reps:', error);
      }
    };

    fetchSalesReps();
  }, [isAdmin]);

  // WebSocket Setup - Based on ScraperControl.tsx
  const setupWebSocketConnection = () => {
    if (socketRef.current) return; // Already connected
    
    try {
      // Extract origin only — passing a path like /api to io() makes Socket.IO
      // treat it as a namespace, causing "Invalid namespace" errors.
      const apiUrl = ((import.meta as any).env.VITE_API_URL as string) || '';
      let socketUrl = window.location.origin;
      try { if (apiUrl) socketUrl = new URL(apiUrl).origin; } catch { /* use default */ }

      socketRef.current = io(socketUrl, {
        path: "/socket.io/",
        transports: ["polling", "websocket"],
        upgrade: true,
        rememberUpgrade: true,
        secure: window.location.protocol === "https:",
        rejectUnauthorized: false,
        timeout: 20000,
        forceNew: true,
        auth: { token: localStorage.getItem("token") },
      });

      socketRef.current.on("connect_error", (err: Error) => console.warn("socket", err.message));

      socketRef.current.on("connect", () => {
        console.log("🔌 Connected to bulk scraper WebSocket");
        setScraperStatus(prev => {
          const newStatus = { ...prev, isConnected: true, isRunning: true };
          console.log('🔍 BulkURLImport: Setting local scraperStatus to:', newStatus);
          
          // Update global context with the correct status
          const globalStatus = { 
            isRunning: newStatus.isRunning, 
            processedCount: newStatus.processedCount, 
            totalCount: newStatus.totalCount 
          };
          console.log('🔍 BulkURLImport: Updating global context with:', globalStatus);
          
          onScraperStatusUpdate?.(globalStatus);
          handleScraperStatusUpdate(globalStatus);
          return newStatus;
        });
        socketRef.current?.emit("join-scraper-room");
      });

      socketRef.current.on("disconnect", (reason) => {
        console.log("🔌 Disconnected from bulk scraper WebSocket:", reason);
        setScraperStatus(prev => {
          const newStatus = { ...prev, isConnected: false, isRunning: false };
          onScraperStatusUpdate?.({ 
            isRunning: newStatus.isRunning, 
            processedCount: newStatus.processedCount, 
            totalCount: newStatus.totalCount 
          });
          handleScraperStatusUpdate({ 
            isRunning: newStatus.isRunning, 
            processedCount: newStatus.processedCount, 
            totalCount: newStatus.totalCount 
          });
          return newStatus;
        });
      });

      socketRef.current.on("page-update", (data: {
        url: string;
        html: string;
        contactId?: string;
        timestamp: string;
      }) => {
        try {
          console.log("📄 Bulk scraper page update:", data.url, "HTML length:", data.html?.length || 0);

          // Validate data
          if (!data || typeof data.url !== "string" || typeof data.html !== "string") {
            console.warn("⚠️ Invalid page update data received:", data);
            return;
          }

          // Clear previous contact data if new contact
          if (data.contactId && currentContactIdRef.current && data.contactId !== currentContactIdRef.current) {
            console.log("🔄 New contact detected, clearing previous contact data");
            setScraperStatus(prev => ({ ...prev, currentContactData: null }));
          }

          // Update current contact ID reference
          if (data.contactId) {
            currentContactIdRef.current = data.contactId;
          }

          setScraperStatus(prev => ({ 
            ...prev, 
            currentPage: data,
            isRunning: true
          }));
        } catch (error) {
          console.error("❌ Error processing bulk page update:", error);
        }
      });

      socketRef.current.on("contact-completed", (data: {
        contactId: string;
        contactData: ContactData;
        timestamp: string;
      }) => {
        try {
          console.log("✅ Bulk contact completed:", data.contactData);

          if (!data || !data.contactData) {
            console.warn("⚠️ Invalid contact completion data received:", data);
            return;
          }

          setScraperStatus(prev => {
            const newProcessedCount = prev.processedCount + 1;
            
            // Check if all contacts are processed using the updated count
             if (newProcessedCount >= prev.totalCount) {
               console.log("🏁 All bulk contacts completed");
               
               const newStatus = {
                 ...prev,
                 currentContactData: data.contactData,
                 processedCount: newProcessedCount,
                 isRunning: false
               };
               
               onScraperStatusUpdate?.({ 
                 isRunning: newStatus.isRunning, 
                 processedCount: newStatus.processedCount, 
                 totalCount: newStatus.totalCount 
               });
               
               handleScraperStatusUpdate({ 
                 isRunning: newStatus.isRunning, 
                 processedCount: newStatus.processedCount, 
                 totalCount: newStatus.totalCount 
               });
               
               return newStatus;
             }
            
            const newStatus = {
              ...prev,
              currentContactData: data.contactData,
              processedCount: newProcessedCount
            };
            
            onScraperStatusUpdate?.({ 
              isRunning: newStatus.isRunning, 
              processedCount: newStatus.processedCount, 
              totalCount: newStatus.totalCount 
            });
            
            handleScraperStatusUpdate({ 
              isRunning: newStatus.isRunning, 
              processedCount: newStatus.processedCount, 
              totalCount: newStatus.totalCount 
            });
            
            return newStatus;
          });
        } catch (error) {
          console.error("❌ Error processing bulk contact completion:", error);
        }
      });

      socketRef.current.on("cleanup-contact-data", (data: { 
        contactId: string; 
        timestamp: string 
      }) => {
        try {
          console.log("🧹 Cleaning up bulk contact data:", data.contactId);
          // Keep page visible, just log the cleanup
        } catch (error) {
          console.error("❌ Error processing bulk cleanup:", error);
        }
      });

      socketRef.current.on("connect_error", (error) => {
        console.error("❌ Bulk WebSocket connection error:", error);
        setScraperStatus(prev => ({ ...prev, isConnected: false }));
      });

      socketRef.current.on("error", (error) => {
        console.error("❌ Bulk WebSocket error:", error);
      });

    } catch (error) {
      console.error("❌ Error setting up bulk WebSocket connection:", error);
      setScraperStatus(prev => ({ ...prev, isConnected: false }));
    }
  };

  // Start Import Function
  const handleStartImport = async () => {
    const urls = getValidUrls();
    
    if (urls.length === 0) {
      alert('Si prega di inserire almeno un URL di profilo LinkedIn valido');
      return;
    }

    setIsProcessing(true);
    setScraperStatus(prev => {
      const newStatus = { 
        ...prev, 
        totalCount: urls.length, 
        processedCount: 0,
        currentPage: null,
        isRunning: true
      };
      
      console.log('🔍 BulkURLImport: handleStartImport - Setting local scraperStatus to:', newStatus);
      
      // Update global context with the correct status
      const globalStatus = { 
        isRunning: newStatus.isRunning, 
        processedCount: newStatus.processedCount, 
        totalCount: newStatus.totalCount 
      };
      console.log('🔍 BulkURLImport: handleStartImport - Updating global context with:', globalStatus);
      
      onScraperStatusUpdate?.(globalStatus);
      handleScraperStatusUpdate(globalStatus);
      
      return newStatus;
    });

    try {
      // Build request data with optional salesRepId for admins
      const requestData: { urls: string[]; salesRepId?: string | null } = {
        urls: urls
      };

      // Only admins can specify salesRepId
      if (isAdmin) {
        if (selectedSalesRepId === 'unassigned') {
          requestData.salesRepId = null;
        } else if (selectedSalesRepId !== 'current') {
          requestData.salesRepId = selectedSalesRepId;
        }
        // If 'current', don't send salesRepId - backend will use req.user._id
      }

      console.log(`🚀 Starting bulk import for ${urls.length} URLs`);
      const response = await axios.post('/api/scraper/start-url-import', requestData);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Impossibile avviare l\'importazione massiva');
      }

      console.log("✅ Bulk import started successfully");
      setShowScraperProgress(true);
      setupWebSocketConnection();
      
    } catch (error: any) {
      console.error('❌ Error starting bulk import:', error);
      alert('Errore durante l\'avvio dell\'importazione massiva: ' + (error.response?.data?.message || error.message));
      setIsProcessing(false);
    }
  };

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      try {
        if (socketRef.current) {
          console.log("🧹 Cleaning up bulk WebSocket connection...");
          socketRef.current.emit("leave-scraper-room");
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        currentContactIdRef.current = null;
      } catch (error) {
        console.error("❌ Error during bulk WebSocket cleanup:", error);
      }
    };
  }, []);

  // Expose handleStartImport to parent component
  useEffect(() => {
    if (onStartImport) {
      onStartImport(handleStartImport);
    }
  }, [onStartImport, handleStartImport]);

  // Show scraper progress view
  if (showScraperProgress) {
    return (
      <ScraperProgressContainer>
        <ScraperProgressTitle>
          🔍 Scraper Massivo Profili LinkedIn
          {scraperStatus.isRunning && <span style={{ fontSize: '0.8rem', color: '#10b981' }}>● LIVE</span>}
        </ScraperProgressTitle>
        <ScraperProgressDescription>
          Estrazione informazioni di contatto da {scraperStatus.totalCount} profili LinkedIn. Questo processo potrebbe richiedere diversi minuti.
        </ScraperProgressDescription>

        <ScraperStatusCard>
          <StatusRow>
            <StatusLabel>Stato Connessione</StatusLabel>
            <StatusValue $status={scraperStatus.isConnected ? 'connected' : 'disconnected'}>
              {scraperStatus.isConnected ? '🟢 Connesso' : '🔴 Disconnesso'}
            </StatusValue>
          </StatusRow>
          <StatusRow>
            <StatusLabel>Stato Scraper</StatusLabel>
            <StatusValue $status={scraperStatus.isRunning ? 'running' : 'stopped'}>
              {scraperStatus.isRunning ? '🔄 In Esecuzione' : '⏹️ Fermato'}
            </StatusValue>
          </StatusRow>
          <StatusRow>
            <StatusLabel>Pagina Corrente</StatusLabel>
            <StatusValue>{scraperStatus.currentPage?.url || 'In attesa...'}</StatusValue>
          </StatusRow>
          <StatusRow>
            <StatusLabel>Contatti Elaborati</StatusLabel>
            <StatusValue>{scraperStatus.processedCount} / {scraperStatus.totalCount}</StatusValue>
          </StatusRow>
        </ScraperStatusCard>

        {scraperStatus.totalCount > 0 && (
          <>
            <ProgressBarContainer>
              <ProgressBar $progress={(scraperStatus.processedCount / scraperStatus.totalCount) * 100} />
            </ProgressBarContainer>
            <ProgressText>
              {scraperStatus.processedCount} di {scraperStatus.totalCount} profili elaborati 
              ({Math.round((scraperStatus.processedCount / scraperStatus.totalCount) * 100)}%)
            </ProgressText>
          </>
        )}

        {scraperStatus.currentContactData && (
          <CurrentContactCard>
            <ContactCardTitle>
              👤 Ultimo Contatto Elaborato
            </ContactCardTitle>
            <ContactDataGrid>
              <ContactDataItem>
                <ContactDataLabel>Nome</ContactDataLabel>
                <ContactDataValue>
                  {scraperStatus.currentContactData.first_name} {scraperStatus.currentContactData.last_name}
                </ContactDataValue>
              </ContactDataItem>
              <ContactDataItem>
                <ContactDataLabel>Azienda</ContactDataLabel>
                <ContactDataValue>{scraperStatus.currentContactData.company || 'N/D'}</ContactDataValue>
              </ContactDataItem>
              <ContactDataItem>
                <ContactDataLabel>Ruolo</ContactDataLabel>
                <ContactDataValue>{scraperStatus.currentContactData.role || 'N/D'}</ContactDataValue>
              </ContactDataItem>
              <ContactDataItem>
                <ContactDataLabel>Email</ContactDataLabel>
                <ContactDataValue>{scraperStatus.currentContactData.email || 'N/D'}</ContactDataValue>
              </ContactDataItem>
            </ContactDataGrid>
          </CurrentContactCard>
        )}

        {scraperStatus.currentPage && (
          <LivePreviewContainer>
            <LivePreviewTitle>
              🖼️ Anteprima Pagina Live
            </LivePreviewTitle>
            <iframe
              key={scraperStatus.currentPage.timestamp}
              srcDoc={scraperStatus.currentPage.html
                .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
                .replace(/javascript:/gi, "")
                .replace(/on\w+\s*=/gi, "")}
              title="LinkedIn Profile Preview"
              style={{ 
                width: '100%', 
                height: '400px', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px',
                background: 'white' 
              }}
              sandbox=""
            />
          </LivePreviewContainer>
        )}

        {!scraperStatus.isRunning && scraperStatus.processedCount === 0 && (
          <EmptyStateMessage>
            🚀 Pronto per avviare lo scraping massivo. Lo scraper inizierà a elaborare tutti i profili LinkedIn.
          </EmptyStateMessage>
        )}

        <ControlButtonsContainer>
          {isScrapingCompleted ? (
            <ControlButton 
              $variant="primary" 
              onClick={handleDone}
            >
              ✅ Fatto
            </ControlButton>
          ) : isScrapingStopped ? (
            <ControlButton 
              $variant="primary" 
              onClick={handleDone}
            >
              ✅ Fatto
            </ControlButton>
          ) : (
            <ControlButton 
              $variant="primary" 
              disabled={true}
            >
              ⏳ Elaborazione in corso...
            </ControlButton>
          )}
          
          {scraperStatus.isRunning && (
            <>
              <ControlButton 
                $variant="danger" 
                onClick={handleStopScraper}
              >
                🛑 Ferma Scraping
              </ControlButton>
              <ControlButton 
                $variant="secondary" 
                onClick={handleHideModal}
              >
                👁️ Nascondi
              </ControlButton>
            </>
          )}
        </ControlButtonsContainer>
      </ScraperProgressContainer>
    );
  }

  // Show input form
  return (
    <BulkImportContainer>
      <BulkImportTitle>Importazione Massiva Contatti</BulkImportTitle>
      <BulkImportDescription>
        Importa più contatti contemporaneamente fornendo gli URL dei profili LinkedIn. Ogni URL deve essere su una riga separata.
      </BulkImportDescription>

      <FormGroup>
        <Label htmlFor="urlsText">URL Profili LinkedIn (uno per riga)</Label>
        <TextArea
          id="urlsText"
          value={urlsText}
          onChange={(e) => setUrlsText(e.target.value)}
          placeholder={`https://www.linkedin.com/in/username1
https://www.linkedin.com/in/username2
https://www.linkedin.com/in/username3`}
          disabled={isProcessing}
        />
        <UrlCounter>
          {validUrls.length} URL LinkedIn valid{validUrls.length !== 1 ? 'i' : 'o'} rilevat{validUrls.length !== 1 ? 'i' : 'o'}
        </UrlCounter>
      </FormGroup>

      {isAdmin && (
        <FormGroup>
          <Label htmlFor="salesRepSelect">Assegna a Commerciale</Label>
          <Select
            id="salesRepSelect"
            value={selectedSalesRepId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedSalesRepId(e.target.value)}
            disabled={isProcessing}
          >
            <option value="current">Utente Corrente (Io)</option>
            <option value="unassigned">Non Assegnato</option>
            {salesReps
              .filter((rep: SalesRep) => rep._id !== user?._id)
              .map((rep: SalesRep) => (
                <option key={rep._id} value={rep._id}>
                  {rep.firstName} {rep.lastName} ({rep.email})
                </option>
              ))}
          </Select>
        </FormGroup>
      )}

      {isProcessing && (
        <ProcessingIndicator>
          <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
            🔄 Avvio processo di importazione massiva...
          </div>
          <div style={{ fontSize: '0.875rem' }}>
            Attendere prego mentre inizializziamo lo scraper dei profili LinkedIn per {validUrls.length} profili.
          </div>
        </ProcessingIndicator>
      )}

      <ControlButtonsContainer>
        <ControlButton
          $variant="primary"
          onClick={handleStartImport}
          disabled={validUrls.length === 0 || isProcessing}
        >
          {isProcessing ? '⏳ Avvio...' : '🚀 Avvia Importazione'}
        </ControlButton>
        <ControlButton
          $variant="secondary"
          onClick={handleCloseModal}
          disabled={isProcessing}
        >
          ❌ Annulla
        </ControlButton>
      </ControlButtonsContainer>
    </BulkImportContainer>
  );
};

export default BulkURLImport;